import { useEffect, useMemo, useState } from 'react'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { ManifestIntent, Reference } from '../api/authoring'
import { payloadId, shown } from '../api/useIntent'
import { asSchema, orderedProperties, resolve, storedValue, type Schema } from '../model/schema'
import { subjectLabel } from '../model/labels'
import { offerLabel } from '../model/manifest'
import { SchemaForm } from './SchemaForm'
import { AuthoringScopeProvider, useAuthoringScope } from './scope'
import type { EditorProps } from './types'

/**
 * One intent, as a form.
 *
 * **The whole payload goes, every time.** These are `set`-shaped events
 * whose fold assigns the struct it is handed, so a payload that omits a
 * member clears it. That is why the starting value is read out of the
 * document's stored fields rather than started empty, and why a property
 * this client cannot draw is still carried: the form's job is to change
 * one thing and restate everything else exactly as it was.
 *
 * One form, one intent, one revision — the same rule the hand-written
 * editors follow. Nothing here batches two intents behind one button,
 * because a revision is what an author undoes and two of them behind one
 * gesture is an undo nobody asked for.
 */
export function IntentForm({
  intent,
  fields,
  heading,
  showName = true,
  holder,
  references,
  busy,
  send,
}: {
  /**
   * Whether the intent's wire name is printed under the form. Build mode
   * shows it, because it is the model's own vocabulary and an operator
   * asks for it; content mode does not, because to a site's owner it is
   * noise.
   */
  showName?: boolean
  intent: ManifestIntent
  /** The document's own fields, which is where a control's starting value comes from. */
  fields: Record<string, unknown>
  /**
   * The section's heading, where what the intent acts on does not say
   * enough on its own.
   *
   * The default is the *subject* — "Title" over the control that
   * retitles — which reads well until two intents share one. Bind and
   * release both act on a domain, so a screen showing them together gets
   * two sections headed "Domain" and has to tell them apart by their
   * buttons. A caller that knows it is doing that passes something
   * better; nothing here can know it.
   *
   * `false` draws none, for a form inside a section that already names
   * what it is about.
   */
  heading?: string | false
  /**
   * The type whose intent this is, and what the node refers to now. A
   * payload member marked `x-edge` is drawn from the holder's declaration
   * of that edge and starts on the reference already held under it, so
   * changing a media link's words is not also forgetting its file.
   */
  holder?: string
  references?: Reference[]
  busy: boolean
  send: EditorProps['send']
}) {
  const scope = useAuthoringScope()
  const schema = asSchema(intent.payload)
  const starting = useMemo(
    () => startingValue(schema, fields, references ?? [], scope.tenant),
    [schema, fields, references, scope.tenant],
  )

  // **Structural, not referential.** `fields` is rebuilt on many renders
  // — a node read out of a document is a fresh object each time — so an
  // effect keyed on its identity would reset the form under an author
  // mid-edit. Keyed on what the value *is*, it resets exactly when the
  // stored document changed, which is the behaviour every hand-written
  // editor has.
  const startingKey = stableKey(starting)
  const [value, setValue] = useState(starting)
  useEffect(() => setValue(starting), [startingKey])

  if (!schema) return null

  // The type's own words where it declared any, and the derivation
  // behind them. A heading is still derived from the intent's name: a
  // declaration says what the *button* does, and a section heading is
  // what it acts on, which the payload's own name already says.
  const label = offerLabel(intent)

  // An intent with no members is a command with nothing to state —
  // `website.not_found_cleared` reflects `struct{}` — and it is a button,
  // not a form. Dirty tracking cannot apply to it: its payload is `{}`
  // before and after, so the usual comparison would disable the control
  // permanently. There will be more of these; this is what they *are*,
  // not an accident of one schema.
  const takesNoInput = orderedProperties(schema, schema).length === 0
  const dirty = !takesNoInput && stableKey(value) !== startingKey

  return (
    <Stack spacing={1.5}>
      {heading !== false && <Typography variant="h3">{heading ?? subjectLabel(intent.name)}</Typography>}

      {takesNoInput ? (
        <Typography variant="body2" color="text.secondary">
          There is nothing to fill in. Pressing the button does it.
        </Typography>
      ) : (
        <AuthoringScopeProvider value={{ ...scope, holder: holder ?? scope.holder }}>
          <SchemaForm schema={schema} root={schema} value={value} onChange={setValue} disabled={busy} />
        </AuthoringScopeProvider>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          disabled={busy || (!takesNoInput && !dirty)}
          onClick={() => send(intent.name, takesNoInput ? {} : value).catch(shown)}
        >
          {label}
        </Button>
        {!takesNoInput && (
          <Button disabled={!dirty || busy} onClick={() => setValue(starting)}>
            Discard
          </Button>
        )}
      </Stack>

      {showName && (
        <Typography variant="caption" color="text.secondary">
          {intent.name}
        </Typography>
      )}
    </Stack>
  )
}

/**
 * The payload as the document already holds it.
 *
 * By name, through `storedValue`, including members this form will not
 * draw — an array it cannot render still has to be restated, or sending
 * the payload would clear it.
 */
function startingValue(
  schema: Schema | undefined,
  fields: Record<string, unknown>,
  references: Reference[],
  tenant: string,
): Record<string, unknown> {
  if (!schema) return {}

  const value: Record<string, unknown> = {}
  for (const property of orderedProperties(schema, schema)) {
    // A reference is not a field, so it starts on the edge the node holds
    // under the marker's name, restated the way a payload states it.
    const edge = resolve(property.schema, schema)?.['x-edge']
    if (edge) {
      const held = references.find((reference) => reference.name === edge)
      if (held) {
        value[property.name] = {
          edgeId: held.edgeId,
          name: held.name,
          id: payloadId(tenant, held.id),
          ...(held.revision !== undefined ? { revision: held.revision } : {}),
        }
      }
      continue
    }
    const stored = storedValue(fields, property)
    if (stored !== undefined) value[property.name] = stored
  }
  return value
}

/**
 * A payload reduced to what it will actually say on the wire, for
 * comparison only.
 *
 * **A zero value and an absent member are the same document.** These
 * schemas are reflected off Go structs, so a field a document has never
 * held reads back as `""` or `false` — and a control the author touched
 * and put back reports exactly that. Comparing raw, every optional field
 * on a freshly created component would read dirty the moment the form
 * mounted, and Discard would be live before anybody had typed. Keys are
 * sorted for the same reason: two payloads that differ only in the order
 * their members were set are one payload.
 *
 * Note what is *not* normalised: `0`. A number control emits nothing
 * until an author types, so a zero here was typed on purpose.
 */
function comparable(value: unknown): unknown {
  if (value === '' || value === false || value === undefined) return undefined
  if (Array.isArray(value) || typeof value !== 'object' || value === null) return value

  const members = Object.entries(value as Record<string, unknown>)
    .map(([name, member]) => [name, comparable(member)] as const)
    .filter(([, member]) => member !== undefined)
    .sort(([left], [right]) => left.localeCompare(right))

  return members.length === 0 ? undefined : Object.fromEntries(members)
}

function stableKey(value: Record<string, unknown>): string {
  return JSON.stringify(comparable(value)) ?? '{}'
}
